import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../state/auth.js';
import { Badge } from '../../components/ui.js';

/**
 * Admin console shell.
 *
 * Visually separated from the storefront on purpose: an operator should never be
 * unsure which mode they are in. The banner at the top states it, and the
 * navigation is a fixed sidebar rather than the storefront's horizontal header.
 *
 * The nav is filtered by role. A manager does not see a link to a page they
 * cannot use — and because the API enforces the same hierarchy, hiding the link
 * is a courtesy, not the control.
 */
export function AdminLayout() {
  const { user } = useAuth();

  const items = [
    { to: '/admin', label: 'Overview', end: true, minRole: 'manager' as const },
    { to: '/admin/products', label: 'Products', end: false, minRole: 'manager' as const },
    { to: '/admin/orders', label: 'Orders', end: false, minRole: 'manager' as const },
  ];

  const rank = { customer: 0, manager: 1, admin: 2 };
  const visible = items.filter((item) => rank[user?.role ?? 'customer'] >= rank[item.minRole]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-raised px-4 py-3">
        <div className="flex items-center gap-3">
          <Badge tone="accent">Admin console</Badge>
          <p className="text-sm text-muted">
            Changes here affect the live catalogue and customer orders.
          </p>
        </div>
        <p className="text-sm text-muted">
          Signed in as <span className="font-medium text-ink">{user?.email}</span>
        </p>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[13rem_1fr]">
        <nav aria-label="Admin sections" className="lg:sticky lg:top-24 lg:self-start">
          <ul className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
            {visible.map((item) => (
              <li key={item.to} className="shrink-0">
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      'block whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-raised hover:text-ink',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
